import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MARKETING_HEAD_ATTR = 'data-audiograph-marketing-head';
const MARKETING_BASE_PATH = '/marketing-v5/';

const PAGE_FILES: Record<string, string> = {
  '/': 'index.html',
  '/index.html': 'index.html',
  '/contact': 'contact.html',
  '/contact.html': 'contact.html',
  '/gallery': 'gallery.html',
  '/gallery.html': 'gallery.html',
  '/services': 'services.html',
  '/services.html': 'services.html',
  '/services/android-head-unit-installation-cavite': 'services/android-head-unit-installation-cavite.html',
  '/services/android-head-unit-installation-cavite.html': 'services/android-head-unit-installation-cavite.html',
  '/services/wireless-carplay-android-auto': 'services/wireless-carplay-android-auto.html',
  '/services/wireless-carplay-android-auto.html': 'services/wireless-carplay-android-auto.html',
  '/services/360-reverse-camera-installation': 'services/360-reverse-camera-installation.html',
  '/services/360-reverse-camera-installation.html': 'services/360-reverse-camera-installation.html',
  '/services/dashcam-car-audio-installation': 'services/dashcam-car-audio-installation.html',
  '/services/dashcam-car-audio-installation.html': 'services/dashcam-car-audio-installation.html',
};

const EXTERNAL_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
const ASSET_EXTENSION = /\.(?:css|js|mjs|png|jpe?g|webp|avif|gif|svg|ico|woff2?|ttf|otf|mp4|webm)(?:[?#].*)?$/i;
const LEGACY_ROOT_ASSET = /^\/(?:assets|css|js|images|fonts)\//i;
const LEGACY_ROOT_FILE = /^\/(?:favicon(?:\.png|\.ico)?|apple-touch-icon\.png|icon-(?:192|512)\.png|manifest\.webmanifest|sw\.js)(?:[?#].*)?$/i;

function marketingPageBase(pageFile: string): URL {
  const slash = pageFile.lastIndexOf('/');
  const directory = slash >= 0 ? pageFile.slice(0, slash + 1) : '';
  return new URL(`${MARKETING_BASE_PATH}${directory}`, window.location.origin);
}

function isLocalRelative(value: string): boolean {
  return Boolean(value) && !value.startsWith('#') && !EXTERNAL_URL.test(value);
}

function rewriteAssetUrl(value: string, pageFile: string): string {
  if (!isLocalRelative(value) || value.startsWith(MARKETING_BASE_PATH)) return value;
  if (!ASSET_EXTENSION.test(value)) return value;

  if (LEGACY_ROOT_ASSET.test(value) || LEGACY_ROOT_FILE.test(value)) {
    return `/marketing-v5${value}`;
  }

  const resolved = new URL(value, marketingPageBase(pageFile));
  if (resolved.origin !== window.location.origin) return value;
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

function rewriteNavigationUrl(value: string, pageFile: string): string {
  if (!isLocalRelative(value)) return value;

  const resolved = new URL(value, marketingPageBase(pageFile));
  if (resolved.origin !== window.location.origin || !resolved.pathname.startsWith(MARKETING_BASE_PATH)) return value;

  let path = resolved.pathname.slice(MARKETING_BASE_PATH.length - 1);
  if (path === '/index.html') path = '/';
  return `${path}${resolved.search}${resolved.hash}`;
}

function rewriteInlineStyle(style: string, pageFile: string): string {
  return style.replace(/url\((['"]?)([^'")]+)\1\)/gi, (_match, quote: string, value: string) => {
    const rewritten = rewriteAssetUrl(value.trim(), pageFile);
    return `url(${quote}${rewritten}${quote})`;
  });
}

function rewriteElementAssets(element: Element, pageFile: string) {
  const tag = element.tagName.toLowerCase();

  for (const attribute of ['src', 'href', 'poster']) {
    const value = element.getAttribute(attribute);
    if (!value) continue;
    const rewritten = tag === 'a' && attribute === 'href'
      ? rewriteNavigationUrl(value, pageFile)
      : rewriteAssetUrl(value, pageFile);
    element.setAttribute(attribute, rewritten);
  }

  const style = element.getAttribute('style');
  if (style) element.setAttribute('style', rewriteInlineStyle(style, pageFile));
}

export function PublicSite() {
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const pageFile = PAGE_FILES[location.pathname] ?? 'index.html';

  useEffect(() => {
    let cancelled = false;
    const addedHeadNodes: Node[] = [];
    const root = rootRef.current;
    const appRoot = root?.parentElement;
    const previousAppRootId = appRoot?.getAttribute('id');

    async function mountMarketingSite() {
      try {
        setError(null);
        const response = await fetch(`${MARKETING_BASE_PATH}${pageFile}`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Marketing site load failed (${response.status})`);
        const html = await response.text();
        if (cancelled || !root) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        document.querySelectorAll(`[${MARKETING_HEAD_ATTR}]`).forEach((node) => node.remove());

        // The packaged marketing pages expect to own their document subtree.
        // Temporarily release the OS shell root id so legacy marketing bundles
        // that look for #root cannot accidentally target the operations shell.
        appRoot?.removeAttribute('id');

        if (parsed.title) {
          document.title = parsed.title;
          const title = document.createElement('title');
          title.setAttribute(MARKETING_HEAD_ATTR, 'true');
          title.textContent = parsed.title;
          document.head.appendChild(title);
          addedHeadNodes.push(title);
        }

        parsed.head.querySelectorAll('meta, link, style, script').forEach((source) => {
          rewriteElementAssets(source, pageFile);
          const node = document.createElement(source.tagName.toLowerCase());
          for (const attribute of Array.from(source.attributes)) node.setAttribute(attribute.name, attribute.value);
          node.textContent = source.textContent;
          node.setAttribute(MARKETING_HEAD_ATTR, 'true');
          document.head.appendChild(node);
          addedHeadNodes.push(node);
        });

        root.innerHTML = parsed.body.innerHTML;
        root.querySelectorAll('[src], [href], [poster], [style]').forEach((element) => rewriteElementAssets(element, pageFile));
        root.querySelectorAll('script').forEach((source) => {
          const script = document.createElement('script');
          for (const attribute of Array.from(source.attributes)) {
            const value = attribute.name === 'src' ? rewriteAssetUrl(attribute.value, pageFile) : attribute.value;
            script.setAttribute(attribute.name, value);
          }
          script.textContent = source.textContent;
          source.replaceWith(script);
        });
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Marketing site could not be loaded');
      }
    }

    void mountMarketingSite();
    return () => {
      cancelled = true;
      addedHeadNodes.forEach((node) => node.parentNode?.removeChild(node));
      if (root) root.innerHTML = '';
      if (appRoot && previousAppRootId !== null && previousAppRootId !== undefined) appRoot.setAttribute('id', previousAppRootId);
    };
  }, [pageFile]);

  if (error) return <main className="marketing-load-error" role="alert">{error}</main>;
  return <div ref={rootRef} className="marketing-site" aria-label="Audiograph public website" />;
}
